import { ExternalLink } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const VERDICT = {
  detected: { label: "Detected", cls: "text-emerald-400 border-emerald-400/50" },
  missed: { label: "Missed", cls: "text-atlas-orange border-atlas-orange/50" },
  error: { label: "Error", cls: "text-atlas-secondary border-white/20" },
};
const CONTROL = {
  quiet: { label: "Control quiet", cls: "text-atlas-secondary" },
  false_alarm: { label: "Control FALSE ALARM", cls: "text-atlas-red" },
  no_data: { label: "Control · no radar pair", cls: "text-atlas-secondary/80" },
};

const radar = (r, date) =>
  `${BACKEND}/api/landslide-dams/radar?lat=${r.lat}&lon=${r.lon}&date=${date}&direction=${r.replay.direction}`;

const Pane = ({ src, label, testid }) => (
  <figure className="relative aspect-square bg-black/60 border border-white/10 overflow-hidden" data-testid={testid}>
    <img src={src} alt={label} loading="lazy" className="w-full h-full object-cover" />
    <figcaption className="absolute top-0 left-0 px-2 py-1 bg-atlas-void/80 text-[9px] font-mono uppercase tracking-widest text-white">
      {label}
    </figcaption>
  </figure>
);

export const RecordCard = ({ r, i }) => {
  const v = VERDICT[r.verdict] || VERDICT.error;
  const c = CONTROL[r.control?.verdict] || CONTROL.no_data;
  const rp = r.replay || {};
  const ok = rp.status === "ok";
  return (
    <article className="border border-white/10 bg-atlas-surface/80 atlas-rise grid md:grid-cols-[1fr_420px]"
      style={{ animationDelay: `${Math.min(i, 8) * 60}ms` }} data-testid={`dam-case-${r.id}`}>
      <div className="p-5 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 border ${v.cls}`} data-testid={`dam-case-verdict-${r.id}`}>{v.label}</span>
          <span className={`text-[10px] font-mono uppercase tracking-widest ${c.cls}`} data-testid={`dam-case-control-${r.id}`}>{c.label}</span>
        </div>
        <h3 className="text-xl font-display text-white leading-tight">{r.name}</h3>
        <p className="text-[11px] font-mono text-atlas-secondary mb-3">
          {r.river} · {r.country} · dam {r.formed}{r.gone ? ` → gone ${r.gone} (${r.lifetime_days} d)` : " · persisted"}
        </p>
        <p className="text-[13px] text-white mb-2" data-testid={`dam-case-reason-${r.id}`}>{r.reason}</p>
        <p className="text-[11px] text-atlas-secondary mb-3">{r.note}</p>
        <div className="flex flex-wrap gap-1 mb-3" data-testid={`dam-case-checks-${r.id}`}>
          {(rp.checks || []).map((k) => (
            <span key={k.now_date} title={`${k.ref_date} → ${k.now_date} · ${k.lake_km2} km²`}
              className={`text-[9px] font-mono px-1.5 py-0.5 border ${k.level ? "border-emerald-400/60 text-emerald-300" : "border-white/10 text-atlas-secondary"}`}>
              {k.now_date.slice(5)}{k.level ? ` · ${k.level}` : ""}
            </span>
          ))}
          {!rp.checks?.length && <span className="text-[9px] font-mono text-atlas-secondary/85">No Sentinel-1 pass between dam and breach</span>}
        </div>
        <p className="text-[10px] font-mono text-atlas-secondary/80 mb-3">
          Control {r.control?.window?.[0]} → {r.control?.window?.[1]}
          {r.control?.result ? ` · new lake ${r.control.result.lake_km2} km²` : ""}
        </p>
        <a href={r.source} target="_blank" rel="noopener noreferrer" data-testid={`dam-case-source-${r.id}`}
          className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-atlas-secondary hover:text-white">
          <ExternalLink className="w-3 h-3" /> Published source
        </a>
      </div>
      {ok ? (
        <div className="grid grid-cols-2 gap-1 p-1 bg-black/30">
          <Pane src={radar(r, rp.ref_date)} label={`Before · ${rp.ref_date}`} testid={`dam-case-before-${r.id}`} />
          <Pane src={radar(r, rp.now_date)} label={`After · ${rp.now_date}`} testid={`dam-case-after-${r.id}`} />
        </div>
      ) : (
        <div className="flex items-center justify-center p-6 bg-black/30 text-center text-[11px] font-mono text-atlas-secondary">
          No radar pass while the lake existed{rp.passes_in_window?.length === 0 ? " (revisit gap)" : ""}
        </div>
      )}
    </article>
  );
};
